'use client';
// useInitialSearchLoading.ts
import { useEffect, useRef, useState } from 'react';
import { DynamicRequestBody } from '../../../schemas/DynamicRequestBody';
import { DynamicResponseBody } from '../../../schemas/DynamicResponseBody';

type TypeParamEntry = { parameterId?: number; parameterValues?: unknown[] };

const TYPE_PARAM_KEY = '79' as const;
const HIDE_TRAILING_MS = 300; // 關閉前延後 0.3s
const DEFAULT_DURATION_MS = 1500; // 關閉前延後 1.5s

/** 穩定序列化：鍵排序，避免相同內容不同參考造成誤判 */
const stableStringify = (val: unknown): string => {
    if (val === null || typeof val !== 'object') return JSON.stringify(val);
    if (Array.isArray(val)) return `[${val.map(stableStringify).join(',')}]`;
    const obj = val as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
};

export function useInitialSearchLoading(
    searchParam: string | null | undefined,
    globalFilterStateAfterApply: DynamicRequestBody | undefined,
    applyFilterResults: DynamicResponseBody | undefined
) {
    const firstSearchRef = useRef<string | null>(null);
    const timerRef = useRef<number | null>(null); // 3 秒 fallback
    const hideDelayRef = useRef<number | null>(null); // 0.5 秒關閉延遲
    const [isInitialSearchLoading, setIsInitialSearchLoading] = useState(false);

    // 用內容簽章來判斷 request 是否真的變更
    const prevReqSigRef = useRef<string>(''); // 前一次的 request 內容簽章

    useEffect(() => {
        const clearFallbackTimer = () => {
            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };

        const clearHideDelay = () => {
            if (hideDelayRef.current !== null) {
                window.clearTimeout(hideDelayRef.current);
                hideDelayRef.current = null;
            }
        };

        // 立刻開；並取消任何待執行的「關閉延遲」
        const startLoading = () => {
            clearHideDelay();
            if (!isInitialSearchLoading) setIsInitialSearchLoading(true);
        };

        // 關閉時延後 0.5s；若期間又需要開，startLoading() 會取消這個延遲
        const stopLoadingWithDelay = (duration: number = HIDE_TRAILING_MS) => {
            clearFallbackTimer();
            clearHideDelay();
            if (!isInitialSearchLoading) return; // 已經是關的就不用排延遲
            hideDelayRef.current = window.setTimeout(() => {
                setIsInitialSearchLoading(false);
                hideDelayRef.current = null;
            }, duration);
        };

        // ✅ 新增：帶 3 秒保險的開啟
        const startLoadingWithFallback = (duration: number = DEFAULT_DURATION_MS) => {
            startLoading();
            clearFallbackTimer();
            timerRef.current = window.setTimeout(() => {
                stopLoadingWithDelay();
                timerRef.current = null;
            }, duration);
        };

        // 每次 effect 結尾或早退前都要更新「前次簽章」
        const reqSig = globalFilterStateAfterApply
            ? stableStringify(globalFilterStateAfterApply)
            : '';
        const commit = () => {
            prevReqSigRef.current = reqSig;
        };

        // helpers -------------
        const getTypeParamFromResponse = (
            res?: DynamicResponseBody
        ): TypeParamEntry | undefined => {
            if (!res) return undefined;
            const anyRes = res as Record<string, unknown>;

            const direct = anyRes[TYPE_PARAM_KEY];
            const isParamEntry = (x: unknown): x is TypeParamEntry =>
                !!x &&
                typeof x === 'object' &&
                ('parameterId' in (x as any) || 'parameterValues' in (x as any));
            if (isParamEntry(direct)) return direct;

            const found = Object.values(anyRes).find(
                (v) => isParamEntry(v) && (v as any).parameterId === 79
            );
            return found as TypeParamEntry | undefined;
        };

        const hasTypeParamInRequest = (req?: DynamicRequestBody): boolean => {
            if (!req) return false;
            const anyReq = req as Record<string, unknown>;
            return Object.prototype.hasOwnProperty.call(anyReq, TYPE_PARAM_KEY);
        };
        // ---------------------

        // 只有在「新搜尋」或「request 內容簽章改變」時，才跑覆寫條件
        const isRequestChanged = prevReqSigRef.current !== reqSig;
        const shouldRunOverride = !!globalFilterStateAfterApply && isRequestChanged;

        // ========== A) 覆寫條件（與 searchParam 無關） ==========
        if (shouldRunOverride) {
            const hasTypeInReq = hasTypeParamInRequest(globalFilterStateAfterApply);
            const typeParam = getTypeParamFromResponse(applyFilterResults);

            // A-0) Request 沒帶 Type
            if (!hasTypeInReq) {
                if (!applyFilterResults || !typeParam) {
                    startLoadingWithFallback();
                    commit();
                    return;
                }

                const len = Array.isArray(typeParam.parameterValues)
                    ? typeParam.parameterValues.length
                    : undefined;

                if (typeof len === 'number') {
                    if (len === 1) {
                        startLoadingWithFallback();
                        commit();
                        return;
                    } else {
                        stopLoadingWithDelay(); // 0 或 >1：延後 0.5s 關閉
                        commit();
                        return;
                    }
                }

                startLoading(); // 無法判斷：保守顯示
                commit();
                return;
            }

            // A-1) 已帶 Type，但還沒結果或結果沒有 Type → 維持顯示
            if (!applyFilterResults || !typeParam) {
                startLoadingWithFallback();
                commit();
                return;
            }

            // A-2) 已帶 Type 且結果唯一 → 提前關閉（延後 0.5s）
            const len = Array.isArray(typeParam.parameterValues)
                ? typeParam.parameterValues.length
                : undefined;

            if (len === 1) {
                stopLoadingWithDelay();
                commit();
                return;
            }
            // 其他情況 → 交給後面的 B/C
        }

        // ========== B) 沒有 searchParam：重置（延後 0.5s 關閉） ==========
        if (!searchParam) {
            firstSearchRef.current = null;
            stopLoadingWithDelay();
            commit();
            return;
        }

        // ========== D) searchParam 沒變：直接退出（避免 3 秒 fallback） ==========
        if (firstSearchRef.current === searchParam) {
            commit();
            return;
        }

        // ========== C) 新搜尋：立刻顯示 + 3 秒 fallback ==========
        firstSearchRef.current = searchParam;
        startLoadingWithFallback();
        commit();

        // 清理（可選）
        return () => {
            clearFallbackTimer();
            clearHideDelay();
        };
    }, [searchParam, globalFilterStateAfterApply, applyFilterResults, isInitialSearchLoading]);

    return { isInitialSearchLoading };
}
